// Admin Panel - Enhanced Content Management Component
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Card, CardContent, CardHeader, CardTitle 
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Trash2, Edit, Plus, Save, X, ChevronDown, ChevronRight,
  BookOpen, GraduationCap, Plane, FileText, Users, Award,
  ArrowLeft, Layers, FileQuestion, Clock, Zap, Eye, Copy, Upload, FileSpreadsheet
} from 'lucide-react';
import { cn } from '../lib/utils';

const API = process.env.REACT_APP_BACKEND_URL;

// ==================== STUDENT MANAGEMENT ====================
export function StudentManagement() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});

  const fetchStudents = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/students?search=${search}`, { withCredentials: true });
      setStudents(res.data.students || []);
    } catch (err) {
      console.error('Error fetching students:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudents();
  }, [search]);

  const handleDelete = async (userId) => {
    if (!window.confirm('Bu öğrenciyi silmek istediğinizden emin misiniz? Tüm verileri silinecek!')) return;
    try {
      await axios.delete(`${API}/api/admin/students/${userId}`, { withCredentials: true });
      fetchStudents();
      setSelectedStudent(null);
      alert('Öğrenci silindi!');
    } catch (err) {
      alert('Silme hatası: ' + err.message);
    }
  };

  const handleUpdate = async () => {
    try {
      // Backend'e gönderilecek verileri hazırla
      const updatePayload = {
        name: editData.name,
        email: editData.email,
        xp: editData.xp,
        current_level: editData.level,
        kariyer_progress: {
          unlocked_levels: editData.all_unlocked ? ENGLISH_LEVELS.map(l => l.id) : (editData.unlocked_levels || []),
          all_unlocked: editData.all_unlocked || false
        },
        cabin_progress: {
          unlocked_modules: editData.all_unlocked ? CABIN_MODULES.map(m => m.id) : (editData.unlocked_modules || [])
        },
        video_interview_unlocked: editData.video_interview_unlocked || false,
        psychology_tests_unlocked: editData.psychology_tests_unlocked || false
      };
      
      await axios.put(`${API}/api/admin/students/${selectedStudent.id}`, updatePayload, { withCredentials: true });
      fetchStudents();
      setEditMode(false);
      alert('Öğrenci güncellendi!');
    } catch (err) {
      alert('Güncelleme hatası: ' + err.message);
    }
  };

  const handleResetProgress = async (userId, type) => {
    if (!window.confirm(`${type === 'all' ? 'Tüm' : type} ilerlemeyi sıfırlamak istediğinizden emin misiniz?`)) return;
    try {
      await axios.post(`${API}/api/admin/students/${userId}/reset-progress?education_type=${type}`, {}, { withCredentials: true });
      fetchStudents();
      alert('İlerleme sıfırlandı!');
    } catch (err) {
      alert('Hata: ' + err.message);
    }
  };

  const ENGLISH_LEVELS = [
    { id: 'level_0', name: 'A0 - Başlangıç' },
    { id: 'level_1', name: 'A1 - Temel' },
    { id: 'level_2', name: 'A2 - Ön Orta' },
    { id: 'level_3', name: 'B1 - Orta' },
    { id: 'level_4', name: 'B1-INT - Mülakat' },
    { id: 'level_5', name: 'B2 - Üst Orta' },
    { id: 'level_6', name: 'B2-INT - Mülakat' },
    { id: 'level_7', name: 'C1 - İleri' },
    { id: 'level_8', name: 'C1-INT - Mülakat' },
  ];

  const CABIN_MODULES = Array.from({ length: 16 }, (_, i) => ({
    id: i + 1,
    name: `Modül ${i + 1}`
  }));

  return (
    <div className="p-3 sm:p-6">
      <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">👥 Öğrenci Yönetimi</h2>
      
      {/* Search */}
      <div className="mb-4 sm:mb-6">
        <Input
          placeholder="Öğrenci ara (isim veya email)..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-md"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Student List */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Öğrenciler ({students.length})</CardTitle>
            </CardHeader>
            <CardContent className="p-0 max-h-[400px] sm:max-h-[600px] overflow-y-auto">
              {loading ? (
                <p className="p-4 text-gray-500">Yükleniyor...</p>
              ) : (
                students.map(student => (
                  <div
                    key={student.id}
                    onClick={() => { setSelectedStudent(student); setEditMode(false); setEditData({}); }}
                    className={cn(
                      "p-3 border-b cursor-pointer hover:bg-gray-50 transition-colors",
                      selectedStudent?.id === student.id && "bg-blue-50 border-l-4 border-l-blue-500"
                    )}
                  >
                    <p className="font-medium">{student.name || 'İsimsiz'}</p>
                    <p className="text-sm text-gray-500">{student.email}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">
                        {student.xp || 0} Mil
                      </span>
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                        {student.current_level || 'A0'}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </div>

        {/* Student Detail */}
        <div className="lg:col-span-2">
          {selectedStudent ? (
            <Card>
              <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                <CardTitle>{editMode ? 'Öğrenci Düzenle' : 'Öğrenci Detayı'}</CardTitle>
                <div className="flex gap-2">
                  {editMode ? (
                    <>
                      <Button size="sm" onClick={handleUpdate}>
                        <Save className="w-4 h-4 mr-1" /> Kaydet
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditMode(false)}>
                        <X className="w-4 h-4 mr-1" /> İptal
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button size="sm" variant="outline" onClick={() => { 
                        setEditMode(true); 
                        setEditData({
                          name: selectedStudent.name,
                          email: selectedStudent.email,
                          xp: selectedStudent.xp || 0,
                          level: selectedStudent.current_level || 'A0',
                          unlocked_levels: selectedStudent.kariyer_progress?.unlocked_levels || [],
                          unlocked_modules: selectedStudent.cabin_progress?.unlocked_modules || [],
                          all_unlocked: selectedStudent.kariyer_progress?.all_unlocked || false,
                          video_interview_unlocked: selectedStudent.video_interview_unlocked || false,
                          psychology_tests_unlocked: selectedStudent.psychology_tests_unlocked || false
                        }); 
                      }}>
                        <Edit className="w-4 h-4 mr-1" /> Düzenle
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => handleDelete(selectedStudent.id)}>
                        <Trash2 className="w-4 h-4 mr-1" /> Sil
                      </Button>
                    </>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {editMode ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium">İsim</label>
                        <Input
                          value={editData.name || ''}
                          onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Email</label>
                        <Input
                          value={editData.email || ''}
                          onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium"> Mil</label>
                        <Input
                          type="number"
                          value={editData.xp || 0}
                          onChange={(e) => setEditData({ ...editData, xp: parseInt(e.target.value) || 0 })}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Seviye</label>
                        <select
                          className="w-full border rounded-md p-2"
                          value={editData.level || 'A0'}
                          onChange={(e) => setEditData({ ...editData, level: e.target.value })}
                        >
                          <option value="A0">A0 - Başlangıç</option>
                          <option value="A1">A1 - Temel</option>
                          <option value="A2">A2 - Ön Orta</option>
                          <option value="B1">B1 - Orta</option>
                          <option value="B2">B2 - Üst Orta</option>
                          <option value="C1">C1 - İleri</option>
                        </select>
                      </div>
                    </div>
                    
                    {/* Education Assignment */}
                    <div className="border-t pt-4 mt-4">
                      <h4 className="font-medium mb-3">📚 Eğitim Ataması</h4>
                      
                      <label className="flex items-center gap-2 mb-4 p-2 bg-green-50 rounded">
                        <input
                          type="checkbox"
                          checked={editData.all_unlocked || false}
                          onChange={(e) => setEditData({ ...editData, all_unlocked: e.target.checked })}
                          className="w-4 h-4"
                        />
                        <span className="font-medium text-green-700">🔓 Tüm Eğitimleri Aç</span>
                      </label>
                      
                      {!editData.all_unlocked && (
                        <>
                          <div className="mb-4">
                            <label className="text-sm font-medium block mb-2">İngilizce Seviyeleri</label>
                            <div className="grid grid-cols-3 gap-2">
                              {ENGLISH_LEVELS.map(level => (
                                <label key={level.id} className="flex items-center gap-1 text-sm p-1 hover:bg-gray-50 rounded">
                                  <input
                                    type="checkbox"
                                    checked={(editData.unlocked_levels || []).includes(level.id)}
                                    onChange={(e) => {
                                      const current = editData.unlocked_levels || [];
                                      if (e.target.checked) {
                                        setEditData({ ...editData, unlocked_levels: [...current, level.id] });
                                      } else {
                                        setEditData({ ...editData, unlocked_levels: current.filter(l => l !== level.id) });
                                      }
                                    }}
                                  />
                                  {level.name}
                                </label>
                              ))}
                            </div>
                          </div>
                          
                          <div>
                            <label className="text-sm font-medium block mb-2">Kabin Memurluğu Modülleri</label>
                            <div className="grid grid-cols-4 gap-2">
                              {CABIN_MODULES.map(mod => (
                                <label key={mod.id} className="flex items-center gap-1 text-sm p-1 hover:bg-gray-50 rounded">
                                  <input
                                    type="checkbox"
                                    checked={(editData.unlocked_modules || []).includes(mod.id)}
                                    onChange={(e) => {
                                      const current = editData.unlocked_modules || [];
                                      if (e.target.checked) {
                                        setEditData({ ...editData, unlocked_modules: [...current, mod.id] });
                                      } else {
                                        setEditData({ ...editData, unlocked_modules: current.filter(m => m !== mod.id) });
                                      }
                                    }}
                                  />
                                  {mod.name}
                                </label>
                              ))}
                            </div>
                          </div>
                        </>
                      )}
                      
                      {/* Ek Özellikler */}
                      <div className="mt-4 pt-4 border-t">
                        <h5 className="font-medium text-sm mb-3">🎯 Ek Özellikler</h5>
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 p-2 bg-amber-50 rounded hover:bg-amber-100 transition-colors">
                            <input
                              type="checkbox"
                              checked={editData.video_interview_unlocked || false}
                              onChange={(e) => setEditData({ ...editData, video_interview_unlocked: e.target.checked })}
                              className="w-4 h-4"
                            />
                            <span className="text-amber-700">🎥 Video Mülakat Koçu</span>
                          </label>
                          <label className="flex items-center gap-2 p-2 bg-purple-50 rounded hover:bg-purple-100 transition-colors">
                            <input
                              type="checkbox"
                              checked={editData.psychology_tests_unlocked || false}
                              onChange={(e) => setEditData({ ...editData, psychology_tests_unlocked: e.target.checked })}
                              className="w-4 h-4"
                            />
                            <span className="text-purple-700">🧠 Psikolojik Testler</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-500">İsim</p>
                        <p className="font-medium">{selectedStudent.name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Email</p>
                        <p className="font-medium">{selectedStudent.email}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500"> Mil</p>
                        <p className="font-medium text-yellow-600">{selectedStudent.xp || 0} Mil</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Seviye</p>
                        <p className="font-medium text-blue-600">{selectedStudent.current_level || 'A0'}</p>
                      </div>
                    </div>
                    
                    {/* Açık Eğitimler */}
                    <div className="border-t pt-4">
                      <h4 className="font-medium mb-2">📚 Açık Eğitimler</h4>
                      {selectedStudent.kariyer_progress?.all_unlocked ? (
                        <div className="p-3 bg-green-50 rounded mb-2">
                          <p className="text-green-700 font-medium">✅ Tüm eğitimler açık</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div className="p-3 bg-blue-50 rounded">
                            <p className="text-gray-600 font-medium mb-1">İngilizce Seviyeleri</p>
                            {selectedStudent.kariyer_progress?.unlocked_levels?.length > 0 ? (
                              <p className="text-blue-700">
                                {selectedStudent.kariyer_progress.unlocked_levels.map(l => l.replace('level_', 'Seviye ')).join(', ')}
                              </p>
                            ) : (
                              <p className="text-gray-400">Henüz açılmamış</p>
                            )}
                          </div>
                          <div className="p-3 bg-sky-50 rounded">
                            <p className="text-gray-600 font-medium mb-1">Kabin Modülleri</p>
                            {selectedStudent.cabin_progress?.unlocked_modules?.length > 0 ? (
                              <p className="text-sky-700">
                                Modül {selectedStudent.cabin_progress.unlocked_modules.join(', ')}
                              </p>
                            ) : (
                              <p className="text-gray-400">Henüz açılmamış</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Ek Özellikler Durumu */}
                    <div className="border-t pt-4">
                      <h4 className="font-medium mb-2">🎯 Ek Özellikler</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div className={`p-3 rounded ${selectedStudent.video_interview_unlocked ? 'bg-amber-50' : 'bg-gray-50'}`}>
                          <p className="text-gray-600 font-medium mb-1">🎥 Video Mülakat</p>
                          {selectedStudent.video_interview_unlocked ? (
                            <p className="text-amber-700 font-medium">✅ Açık</p>
                          ) : (
                            <p className="text-gray-400">🔒 Kilitli</p>
                          )}
                        </div>
                        <div className={`p-3 rounded ${selectedStudent.psychology_tests_unlocked ? 'bg-purple-50' : 'bg-gray-50'}`}>
                          <p className="text-gray-600 font-medium mb-1">🧠 Psikolojik Testler</p>
                          {selectedStudent.psychology_tests_unlocked ? (
                            <p className="text-purple-700 font-medium">✅ Açık</p>
                          ) : (
                            <p className="text-gray-400">🔒 Kilitli</p>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Progress Info */}
                    <div className="border-t pt-4">
                      <h4 className="font-medium mb-2">📊 İlerleme</h4>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div className="p-3 bg-blue-50 rounded">
                          <p className="text-gray-600">İngilizce</p>
                          <p className="font-bold text-blue-700">{selectedStudent.kariyer_progress?.completed_lessons?.length || 0} ders</p>
                        </div>
                        <div className="p-3 bg-sky-50 rounded">
                          <p className="text-gray-600">Kabin Memurluğu</p>
                          <p className="font-bold text-sky-700">{selectedStudent.cabin_progress?.completed_lessons?.length || 0} ders</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="border-t pt-4">
                      <h4 className="font-medium mb-2">⚙️ İşlemler</h4>
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" onClick={() => handleResetProgress(selectedStudent.id, 'english')}>
                          İngilizce Sıfırla
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleResetProgress(selectedStudent.id, 'cabin_crew')}>
                          Kabin Sıfırla
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => handleResetProgress(selectedStudent.id, 'all')}>
                          Tümünü Sıfırla
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-12 text-center text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Detay görmek için bir öğrenci seçin</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

// ==================== ENHANCED CONTENT MANAGEMENT ====================
export function ContentManagement({ educationType = 'english' }) {
  const [modules, setModules] = useState([]);
  const [units, setUnits] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [selectedModule, setSelectedModule] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('modules'); // modules, units, lessons, lesson-edit
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [importing, setImporting] = useState(false);
  const [dataSource, setDataSource] = useState('unknown'); // 'database' or 'static'

  // Load content from database first, fallback to static
  const loadContent = async () => {
    setLoading(true);
    try {
      // Try to load from database API
      const res = await axios.get(`${API}/api/curriculum/levels?education_type=${educationType}`, { withCredentials: true });
      
      if (res.data.levels && res.data.levels.length > 0) {
        const levelsArray = res.data.levels.map((level, idx) => ({
          ...level,
          module_id: level.id,
          title: level.level_code ? `${level.level_code}: ${level.title}` : level.title,
          subtitle: level.titleTr || level.subtitle || '',
          unit_count: level.units?.length || 0
        }));
        setModules(levelsArray);
        setDataSource('database');
      } else {
        // Fallback to static files
        await loadStaticContent();
      }
    } catch (err) {
      console.log('Database not available, loading from static files');
      await loadStaticContent();
    } finally {
      setLoading(false);
    }
  };

  // Load from static files (fallback)
  const loadStaticContent = async () => {
    try {
      if (educationType === 'english') {
        const { KARIYER_LEVELS } = await import('../data/kariyerCurriculum');
        const levelsArray = Object.values(KARIYER_LEVELS).map((level, idx) => ({
          ...level,
          module_id: level.id,
          title: `${level.level_code}: ${level.title}`,
          subtitle: level.titleTr,
          unit_count: level.units?.length || 0
        }));
        setModules(levelsArray);
      } else if (educationType === 'cabin_crew') {
        const { cabinCrewCurriculum } = await import('../data/cabinCrewCurriculum');
        const modulesArray = Object.values(cabinCrewCurriculum.modules).map((mod, idx) => ({
          ...mod,
          module_id: mod.id,
          title: `MODÜL ${mod.module_id}: ${mod.title}`,
          unit_count: mod.units?.length || 0
        }));
        setModules(modulesArray);
      }
      setDataSource('static');
    } catch (err) {
      console.error('Error loading static content:', err);
    }
  };

  // Import static content to database
  const importToDatabase = async () => {
    if (!window.confirm('Mevcut müfredat verilerini veritabanına aktarmak istiyor musunuz? Bu işlem mevcut verilerin üzerine yazacak.')) {
      return;
    }
    
    setImporting(true);
    try {
      let levelsData = [];
      
      if (educationType === 'english') {
        const { KARIYER_LEVELS } = await import('../data/kariyerCurriculum');
        levelsData = Object.values(KARIYER_LEVELS).map((level, idx) => ({
          ...level,
          order: idx + 1
        }));
      } else if (educationType === 'cabin_crew') {
        const { cabinCrewCurriculum } = await import('../data/cabinCrewCurriculum');
        levelsData = Object.values(cabinCrewCurriculum.modules).map((mod, idx) => ({
          ...mod,
          order: idx + 1
        }));
      }
      
      const res = await axios.post(`${API}/api/admin/curriculum/import`, {
        education_type: educationType,
        levels: levelsData,
        clear_existing: true
      }, { withCredentials: true });
      
      alert(`Başarılı! ${res.data.inserted} yeni seviye eklendi, ${res.data.updated} seviye güncellendi.`);
      loadContent(); // Reload from database
    } catch (err) {
      console.error('Import error:', err);
      alert('Hata: ' + (err.response?.data?.detail || err.message));
    } finally {
      setImporting(false);
    }
  };

  // Import lesson contents to database
  const importLessonContents = async () => {
    if (!window.confirm('Tüm ders içeriklerini (flashcard, quiz vb.) veritabanına aktarmak istiyor musunuz?')) {
      return;
    }
    
    setImporting(true);
    try {
      let contentsData = [];
      
      if (educationType === 'english') {
        // Import kariyerContents
        const { getAllLessonContents } = await import('../data/kariyerContents');
        const allContents = getAllLessonContents();
        
        // Transform to database format
        for (const [key, content] of Object.entries(allContents)) {
          const parts = key.split('-');
          if (parts.length >= 3) {
            contentsData.push({
              level_id: parts[0],
              unit_id: parts[1],
              lesson_id: parts[2],
              content: content
            });
          }
        }
      } else if (educationType === 'cabin_crew') {
        // Import cabinCrewContents
        const { getAllCabinLessonContents } = await import('../data/cabinCrewContents');
        if (typeof getAllCabinLessonContents === 'function') {
          const allContents = getAllCabinLessonContents();
          for (const [lessonId, content] of Object.entries(allContents)) {
            contentsData.push({
              level_id: 'cabin',
              unit_id: '1',
              lesson_id: lessonId,
              content: content
            });
          }
        }
      }
      
      if (contentsData.length === 0) {
        alert('Aktarılacak ders içeriği bulunamadı.');
        return;
      }
      
      const res = await axios.post(`${API}/api/admin/lesson-contents/import`, {
        education_type: educationType,
        contents: contentsData
      }, { withCredentials: true });
      
      alert(`Başarılı! ${res.data.inserted} yeni içerik eklendi, ${res.data.updated} içerik güncellendi. Toplam: ${res.data.total}`);
    } catch (err) {
      console.error('Import lesson contents error:', err);
      alert('Hata: ' + (err.response?.data?.detail || err.message));
    } finally {
      setImporting(false);
    }
  };

  useEffect(() => {
    loadContent();
    setSelectedModule(null);
    setSelectedUnit(null);
    setSelectedLesson(null);
    setView('modules');
  }, [educationType]);

  const handleSelectModule = async (module) => {
    setSelectedModule(module);
    setSelectedUnit(null);
    setSelectedLesson(null);
    setUnits(module.units || []);
    setView('units');
  };

  const handleSelectUnit = async (unit) => {
    setSelectedUnit(unit);
    setSelectedLesson(null);
    setLessons(unit.lessons || []);
    setView('lessons');
  };

  const handleSelectLesson = (lesson) => {
    setSelectedLesson(lesson);
    setEditData({ ...lesson });
    setView('lesson-edit');
  };

  const handleSaveModule = async () => {
    if (dataSource === 'static') {
      alert('Veritabanına aktarmadan düzenleme yapamazsınız. Önce "Veritabanına Aktar" butonuna tıklayın.');
      return;
    }
    try {
      if (editData.id) {
        await axios.put(`${API}/api/admin/curriculum/level/${editData.id}`, {
          ...editData,
          education_type: educationType
        }, { withCredentials: true });
        alert('Seviye güncellendi!');
      } else {
        await axios.post(`${API}/api/admin/curriculum/level`, {
          ...editData,
          education_type: educationType
        }, { withCredentials: true });
        alert('Seviye oluşturuldu!');
      }
      setEditMode(false);
      setEditData({});
      loadContent();
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleSaveUnit = async () => {
    if (dataSource === 'static') {
      alert('Veritabanına aktarmadan düzenleme yapamazsınız. Önce "Veritabanına Aktar" butonuna tıklayın.');
      return;
    }
    try {
      // Update the module with modified units
      const updatedUnits = [...(selectedModule.units || [])];
      const unitIndex = updatedUnits.findIndex(u => u.unit_id === editData.unit_id || u.id === editData.id);
      
      if (unitIndex >= 0) {
        updatedUnits[unitIndex] = editData;
      } else {
        updatedUnits.push({
          ...editData,
          unit_id: editData.unit_id || `${selectedModule.id}-U${updatedUnits.length + 1}`,
          id: updatedUnits.length + 1
        });
      }
      
      await axios.put(`${API}/api/admin/curriculum/level/${selectedModule.id}`, {
        ...selectedModule,
        units: updatedUnits,
        education_type: educationType
      }, { withCredentials: true });
      
      alert('Ünite kaydedildi!');
      setEditMode(false);
      setEditData({});
      loadContent();
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleSaveLesson = async () => {
    if (dataSource === 'static') {
      alert('Veritabanına aktarmadan düzenleme yapamazsınız. Önce "Veritabanına Aktar" butonuna tıklayın.');
      return;
    }
    try {
      // Update the unit with modified lessons
      const updatedLessons = [...(selectedUnit.lessons || [])];
      const lessonIndex = updatedLessons.findIndex(l => l.lesson_id === editData.lesson_id || l.id === editData.id);
      
      if (lessonIndex >= 0) {
        updatedLessons[lessonIndex] = editData;
      } else {
        updatedLessons.push({
          ...editData,
          lesson_id: editData.lesson_id || `${selectedUnit.unit_id}-L${updatedLessons.length + 1}`,
          id: updatedLessons.length + 1
        });
      }
      
      // Update unit in module
      const updatedUnits = [...(selectedModule.units || [])];
      const unitIndex = updatedUnits.findIndex(u => u.unit_id === selectedUnit.unit_id || u.id === selectedUnit.id);
      if (unitIndex >= 0) {
        updatedUnits[unitIndex] = { ...selectedUnit, lessons: updatedLessons };
      }
      
      await axios.put(`${API}/api/admin/curriculum/level/${selectedModule.id}`, {
        ...selectedModule,
        units: updatedUnits,
        education_type: educationType
      }, { withCredentials: true });
      
      alert('Ders kaydedildi!');
      setEditMode(false);
      loadContent();
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async (type, id) => {
    if (dataSource === 'static') {
      alert('Veritabanına aktarmadan silme yapamazsınız. Önce "Veritabanına Aktar" butonuna tıklayın.');
      return;
    }
    if (!window.confirm('Silmek istediğinizden emin misiniz?')) return;
    
    try {
      if (type === 'level') {
        await axios.delete(`${API}/api/admin/curriculum/level/${id}?education_type=${educationType}`, { withCredentials: true });
        loadContent();
        setView('modules');
      } else if (type === 'unit') {
        const updatedUnits = selectedModule.units.filter(u => u.unit_id !== id && u.id !== id);
        await axios.put(`${API}/api/admin/curriculum/level/${selectedModule.id}`, {
          ...selectedModule,
          units: updatedUnits,
          education_type: educationType
        }, { withCredentials: true });
        loadContent();
        setView('units');
      } else if (type === 'lesson') {
        const updatedLessons = selectedUnit.lessons.filter(l => l.lesson_id !== id && l.id !== id);
        const updatedUnits = selectedModule.units.map(u => 
          (u.unit_id === selectedUnit.unit_id || u.id === selectedUnit.id) 
            ? { ...u, lessons: updatedLessons } 
            : u
        );
        await axios.put(`${API}/api/admin/curriculum/level/${selectedModule.id}`, {
          ...selectedModule,
          units: updatedUnits,
          education_type: educationType
        }, { withCredentials: true });
        loadContent();
        setView('lessons');
      }
      alert('Silindi!');
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.detail || err.message));
    }
  };

  const typeLabels = {
    english: { title: '📚 İngilizce Eğitimi', icon: BookOpen, color: 'text-blue-600' },
    cabin_crew: { title: '✈️ Kabin Memurluğu', icon: Plane, color: 'text-sky-600' }
  };

  const { title, icon: TypeIcon, color } = typeLabels[educationType] || typeLabels.english;

  // Data source indicator and import button
  const renderDataSourceBanner = () => (
    <div className={cn(
      "p-3 rounded-lg mb-4 flex items-center justify-between flex-wrap gap-2",
      dataSource === 'database' ? "bg-green-50 border border-green-200" : "bg-yellow-50 border border-yellow-200"
    )}>
      <div className="flex items-center gap-2">
        {dataSource === 'database' ? (
          <>
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-sm text-green-700 font-medium">✅ Veritabanından okunuyor - Düzenleme aktif</span>
          </>
        ) : (
          <>
            <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
            <span className="text-sm text-yellow-700 font-medium">⚠️ Static dosyalardan okunuyor - Düzenleme için veritabanına aktarın</span>
          </>
        )}
      </div>
      <div className="flex gap-2">
        {dataSource === 'database' && (
          <Button 
            onClick={importLessonContents} 
            disabled={importing}
            variant="outline"
            className="text-sm border-purple-300 text-purple-700 hover:bg-purple-50"
            size="sm"
          >
            {importing ? (
              <>
                <div className="animate-spin w-4 h-4 border-2 border-purple-500 border-t-transparent rounded-full mr-2"></div>
                Aktarılıyor...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-1" />
                Ders İçeriklerini Aktar
              </>
            )}
          </Button>
        )}
        {dataSource === 'static' && (
          <Button 
            onClick={importToDatabase} 
            disabled={importing}
            className="bg-blue-600 hover:bg-blue-700 text-sm"
            size="sm"
          >
            {importing ? (
              <>
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                Aktarılıyor...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-1" />
                Veritabanına Aktar
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );

  const renderBreadcrumb = () => (
    <div className="flex items-center gap-2 text-sm mb-4">
      <button onClick={() => { setView('modules'); setSelectedModule(null); }} className="text-blue-600 hover:underline">
        Modüller
      </button>
      {selectedModule && (
        <>
          <ChevronRight className="w-4 h-4 text-gray-400" />
          <button onClick={() => { setView('units'); setSelectedUnit(null); }} className="text-blue-600 hover:underline">
            {selectedModule.title}
          </button>
        </>
      )}
      {selectedUnit && (
        <>
          <ChevronRight className="w-4 h-4 text-gray-400" />
          <button onClick={() => { setView('lessons'); setSelectedLesson(null); }} className="text-blue-600 hover:underline">
            {selectedUnit.title}
          </button>
        </>
      )}
      {selectedLesson && (
        <>
          <ChevronRight className="w-4 h-4 text-gray-400" />
          <span className="text-gray-600">{selectedLesson.title}</span>
        </>
      )}
    </div>
  );

  // Modules View
  const renderModulesView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {modules.map(module => (
        <Card 
          key={module.module_id} 
          className="cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02]"
          onClick={() => handleSelectModule(module)}
        >
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <span className="text-3xl">{module.icon}</span>
              <div className="flex-1">
                <h3 className="font-bold">{module.level_code || ''} {module.title}</h3>
                <p className="text-sm text-gray-500">{module.subtitle}</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                    {module.unit_count ?? module.units?.length ?? 0} ünite
                  </span>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </div>
          </CardContent>
        </Card>
      ))}
      
      {/* Add New Module */}
      <Card 
        className="cursor-pointer hover:shadow-lg transition-all border-dashed border-2 border-gray-300 hover:border-blue-400"
        onClick={() => { setEditMode(true); setEditData({ module_id: '', title: '', subtitle: '', icon: '📚' }); }}
      >
        <CardContent className="p-4 flex items-center justify-center h-full min-h-[100px]">
          <div className="text-center text-gray-500">
            <Plus className="w-8 h-8 mx-auto mb-2" />
            <p>Yeni Modül Ekle</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Units View
  const renderUnitsView = () => (
    <div>
      <div className="flex items-center justify-between mb-4">
        <Button variant="outline" size="sm" onClick={() => { setView('modules'); setSelectedModule(null); }}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Geri
        </Button>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => { setEditMode(true); setEditData({ unit_id: '', title: '', icon: '📖' }); }}>
            <Plus className="w-4 h-4 mr-1" /> Yeni Ünite
          </Button>
          <Button size="sm" variant="outline" onClick={() => { setEditMode(true); setEditData(selectedModule); }}>
            <Edit className="w-4 h-4 mr-1" /> Modülü Düzenle
          </Button>
          <Button size="sm" variant="destructive" onClick={() => handleDelete('modules', selectedModule.module_id)}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <span className="text-4xl">{selectedModule?.icon}</span>
            <div>
              <h2 className="text-xl font-bold">{selectedModule?.level_code} {selectedModule?.title}</h2>
              <p className="text-gray-500">{selectedModule?.subtitle}</p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <div className="space-y-3">
        {(units.length > 0 ? units : selectedModule?.units || []).map((unit, idx) => (
          <Card 
            key={unit.unit_id || idx} 
            className="cursor-pointer hover:shadow-md transition-all"
            onClick={() => handleSelectUnit(unit)}
          >
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-2xl">{unit.icon || '📖'}</span>
                <div>
                  <h3 className="font-medium">Ünite {unit.id || idx + 1}: {unit.title}</h3>
                  <p className="text-sm text-gray-500">{unit.lesson_count ?? unit.lessons?.length ?? 0} ders</p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );

  // Lessons View
  const renderLessonsView = () => (
    <div>
      <div className="flex items-center justify-between mb-4">
        <Button variant="outline" size="sm" onClick={() => { setView('units'); setSelectedUnit(null); }}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Geri
        </Button>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => { setEditMode(true); setEditData({ lesson_id: '', title: '', type: 'lesson', duration: 15, xp: 30 }); }}>
            <Plus className="w-4 h-4 mr-1" /> Yeni Ders
          </Button>
          <Button size="sm" variant="outline" onClick={() => { setEditMode(true); setEditData(selectedUnit); }}>
            <Edit className="w-4 h-4 mr-1" /> Üniteyi Düzenle
          </Button>
        </div>
      </div>
      
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <span className="text-3xl">{selectedUnit?.icon || '📖'}</span>
            <div>
              <h2 className="text-lg font-bold">{selectedUnit?.title}</h2>
              <p className="text-sm text-gray-500">{selectedUnit?.description}</p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      <div className="space-y-2">
        {(lessons.length > 0 ? lessons : selectedUnit?.lessons || []).map((lesson, idx) => (
          <Card 
            key={lesson.lesson_id || idx} 
            className="cursor-pointer hover:shadow-md transition-all"
            onClick={() => handleSelectLesson(lesson)}
          >
            <CardContent className="p-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={cn(
                  "w-10 h-10 rounded-lg flex items-center justify-center",
                  lesson.type === 'quiz' ? 'bg-purple-100' :
                  lesson.type === 'exam' ? 'bg-red-100' :
                  lesson.type === 'practice' ? 'bg-green-100' :
                  'bg-blue-100'
                )}>
                  {lesson.type === 'quiz' ? <FileQuestion className="w-5 h-5 text-purple-600" /> :
                   lesson.type === 'exam' ? <GraduationCap className="w-5 h-5 text-red-600" /> :
                   lesson.type === 'practice' ? <Layers className="w-5 h-5 text-green-600" /> :
                   <BookOpen className="w-5 h-5 text-blue-600" />}
                </div>
                <div>
                  <h4 className="font-medium">{lesson.title}</h4>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {lesson.duration || 15} dk
                    </span>
                    <span className="flex items-center gap-1 text-yellow-600">
                      <Zap className="w-3 h-3" /> +{lesson.xp || 30} Mil
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={cn(
                  "text-xs px-2 py-0.5 rounded",
                  lesson.type === 'quiz' ? 'bg-purple-100 text-purple-700' :
                  lesson.type === 'exam' ? 'bg-red-100 text-red-700' :
                  lesson.type === 'practice' ? 'bg-green-100 text-green-700' :
                  'bg-blue-100 text-blue-700'
                )}>
                  {lesson.type}
                </span>
                <ChevronRight className="w-5 h-5 text-gray-400" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );

  // Lesson Edit View - Gerçek içeriklerle
  const [lessonContent, setLessonContent] = useState(null);
  const [contentLoading, setContentLoading] = useState(false);
  const [contentImported, setContentImported] = useState(false);
  const [contentKey, setContentKey] = useState(null);

  // Ders içeriğini yükle (static veya database'den)
  const loadLessonContent = async (lesson) => {
    setContentLoading(true);
    setLessonContent(null);
    setContentKey(null);
    
    try {
      // İçerik key'i - lesson_id kullan (A0-U1-L1 formatında)
      const key = lesson.lesson_id || lesson.id;
      
      // Önce veritabanından dene
      try {
        const dbRes = await axios.get(`${API}/api/lesson-contents/${key}`, { withCredentials: true });
        if (dbRes.data.content) {
          setLessonContent(dbRes.data.content);
          setContentKey(key);
          setContentImported(true);
          setContentLoading(false);
          return;
        }
      } catch (err) {
        // DB'de yok, static'ten devam
      }
      
      // Static dosyadan yükle - kariyerContents.js kullan (A0-U1-L1 formatı)
      const { LESSON_CONTENTS } = await import('../data/kariyerContents');
      
      if (LESSON_CONTENTS[key]) {
        setLessonContent({ ...LESSON_CONTENTS[key] });
        setContentKey(key);
        setContentImported(false);
      } else {
        // Kabin memurluğu içerikleri için de dene
        const { CABIN_CREW_CONTENTS } = await import('../data/lessonContentsCabinCrew');
        
        // Kabin memurluğu için farklı key formatı dene (1-1, 2-1 gibi)
        const unitNum = lesson.unit_id?.match(/\d+/)?.[0] || lesson.id?.toString().split('-')[0];
        const lessonNum = lesson.id?.toString().split('-').pop() || lesson.id;
        const cabinKey = `${unitNum}-${lessonNum}`;
        
        if (CABIN_CREW_CONTENTS && CABIN_CREW_CONTENTS[cabinKey]) {
          setLessonContent({ ...CABIN_CREW_CONTENTS[cabinKey], key: cabinKey });
          setContentKey(cabinKey);
          setContentImported(false);
        }
      }
      
    } catch (err) {
      console.error('Error loading lesson content:', err);
    }
    setContentLoading(false);
  };

  // İçeriği veritabanına aktar
  const importLessonContent = async () => {
    if (!lessonContent) return;
    
    try {
      const contentKey = editData.content_key || editData.lesson_id || editData.id || lessonContent.key;
      await axios.put(`${API}/api/admin/lesson-contents/${contentKey}`, lessonContent, { withCredentials: true });
      alert('İçerik veritabanına aktarıldı!');
      setContentImported(true);
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.detail || err.message));
    }
  };

  // İçerik kaydet
  const saveLessonContent = async () => {
    if (!lessonContent) return;
    
    try {
      const contentKey = editData.content_key || editData.lesson_id || editData.id || lessonContent.key;
      await axios.put(`${API}/api/admin/lesson-contents/${contentKey}`, lessonContent, { withCredentials: true });
      alert('İçerik kaydedildi!');
      setContentImported(true);
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.detail || err.message));
    }
  };

  // Ders seçildiğinde içeriği yükle
  useEffect(() => {
    if (selectedLesson && view === 'lesson-edit') {
      loadLessonContent(selectedLesson);
    }
  }, [selectedLesson, view]);

  const renderLessonEditView = () => (
    <div>
      <div className="flex items-center justify-between mb-4">
        <Button variant="outline" size="sm" onClick={() => { setView('lessons'); setSelectedLesson(null); setEditMode(false); setLessonContent(null); }}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Geri
        </Button>
        <div className="flex gap-2">
          <Button size="sm" onClick={handleSaveLesson} className="bg-green-600 hover:bg-green-700">
            <Save className="w-4 h-4 mr-1" /> Ders Bilgilerini Kaydet
          </Button>
          <Button size="sm" variant="destructive" onClick={() => handleDelete('lesson', editData.lesson_id || editData.id)}>
            <Trash2 className="w-4 h-4 mr-1" /> Sil
          </Button>
        </div>
      </div>
      
      {/* Ders Bilgileri */}
      <Card className="mb-6">
        <CardHeader className="bg-blue-50">
          <CardTitle className="text-lg">📝 Ders Bilgileri</CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Ders ID</label>
              <Input
                value={editData.lesson_id || editData.id || ''}
                onChange={(e) => setEditData({ ...editData, lesson_id: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Başlık</label>
              <Input
                value={editData.title || ''}
                onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Tip</label>
              <select
                className="w-full border rounded-md p-2 mt-1"
                value={editData.type || 'grammar'}
                onChange={(e) => setEditData({ ...editData, type: e.target.value })}
              >
                <option value="grammar">📖 Gramer</option>
                <option value="vocabulary">📚 Kelime</option>
                <option value="practice">✏️ Uygulama</option>
                <option value="quiz">❓ Quiz</option>
                <option value="exam">🎓 Sınav</option>
                <option value="flashcard">🃏 Flashcard</option>
                <option value="voice">🎤 Konuşma</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-sm font-medium text-gray-700">Süre</label>
                <Input type="number" value={editData.duration || 15} onChange={(e) => setEditData({ ...editData, duration: parseInt(e.target.value) || 15 })} className="mt-1" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">XP</label>
                <Input type="number" value={editData.xp || 30} onChange={(e) => setEditData({ ...editData, xp: parseInt(e.target.value) || 30 })} className="mt-1" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Ders İçeriği */}
      <Card>
        <CardHeader className="bg-green-50">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">📄 Ders İçeriği (Öğrencinin Gördüğü)</CardTitle>
            <div className="flex items-center gap-2">
              {contentImported ? (
                <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">✅ Veritabanında</span>
              ) : lessonContent ? (
                <>
                  <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">⚠️ Static</span>
                  <Button size="sm" onClick={importLessonContent} className="bg-blue-600 hover:bg-blue-700">
                    <Upload className="w-4 h-4 mr-1" /> DB'ye Aktar
                  </Button>
                </>
              ) : null}
              {lessonContent && (
                <Button size="sm" onClick={saveLessonContent} className="bg-green-600 hover:bg-green-700">
                  <Save className="w-4 h-4 mr-1" /> İçeriği Kaydet
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-4">
          {contentLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-gray-500">İçerik yükleniyor...</p>
            </div>
          ) : !lessonContent ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <p className="text-gray-500 mb-4">Bu ders için içerik bulunamadı.</p>
              <Button onClick={() => setLessonContent({ type: 'flashcard', title: editData.title, cards: [] })}>
                <Plus className="w-4 h-4 mr-1" /> Yeni İçerik Oluştur
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {/* İçerik Tipi ve Başlık */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">İçerik Tipi</label>
                  <select
                    className="w-full border rounded-md p-2 mt-1"
                    value={lessonContent.type || 'flashcard'}
                    onChange={(e) => setLessonContent({ ...lessonContent, type: e.target.value })}
                  >
                    <option value="flashcard">🃏 Flashcard (Kart)</option>
                    <option value="voice">🎤 Sesli İfadeler</option>
                    <option value="quiz">❓ Quiz</option>
                    <option value="dialogue">💬 Diyalog</option>
                    <option value="matching">🔗 Eşleştirme</option>
                    <option value="fill">✏️ Boşluk Doldurma</option>
                    <option value="text">📝 Metin</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">İçerik Başlığı</label>
                  <Input
                    value={lessonContent.title || ''}
                    onChange={(e) => setLessonContent({ ...lessonContent, title: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Flashcard İçeriği */}
              {lessonContent.type === 'flashcard' && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700">Kartlar ({lessonContent.cards?.length || 0} adet)</label>
                    <Button size="sm" variant="outline" onClick={() => setLessonContent({
                      ...lessonContent,
                      cards: [...(lessonContent.cards || []), { front: '', back: '' }]
                    })}>
                      <Plus className="w-4 h-4 mr-1" /> Kart Ekle
                    </Button>
                  </div>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {(lessonContent.cards || []).map((card, idx) => (
                      <div key={idx} className="flex gap-2 items-center p-2 bg-gray-50 rounded">
                        <span className="text-xs text-gray-400 w-6">{idx + 1}</span>
                        <Input
                          value={card.front || ''}
                          onChange={(e) => {
                            const cards = [...lessonContent.cards];
                            cards[idx] = { ...cards[idx], front: e.target.value };
                            setLessonContent({ ...lessonContent, cards });
                          }}
                          placeholder="Ön (İngilizce)"
                          className="flex-1"
                        />
                        <Input
                          value={card.back || ''}
                          onChange={(e) => {
                            const cards = [...lessonContent.cards];
                            cards[idx] = { ...cards[idx], back: e.target.value };
                            setLessonContent({ ...lessonContent, cards });
                          }}
                          placeholder="Arka (Türkçe)"
                          className="flex-1"
                        />
                        <Button size="sm" variant="ghost" onClick={() => {
                          const cards = lessonContent.cards.filter((_, i) => i !== idx);
                          setLessonContent({ ...lessonContent, cards });
                        }}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Voice/Phrases İçeriği */}
              {lessonContent.type === 'voice' && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700">İfadeler ({lessonContent.phrases?.length || 0} adet)</label>
                    <Button size="sm" variant="outline" onClick={() => setLessonContent({
                      ...lessonContent,
                      phrases: [...(lessonContent.phrases || []), { text: '', translation: '' }]
                    })}>
                      <Plus className="w-4 h-4 mr-1" /> İfade Ekle
                    </Button>
                  </div>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {(lessonContent.phrases || []).map((phrase, idx) => (
                      <div key={idx} className="flex gap-2 items-center p-2 bg-gray-50 rounded">
                        <span className="text-xs text-gray-400 w-6">{idx + 1}</span>
                        <Input
                          value={phrase.text || ''}
                          onChange={(e) => {
                            const phrases = [...lessonContent.phrases];
                            phrases[idx] = { ...phrases[idx], text: e.target.value };
                            setLessonContent({ ...lessonContent, phrases });
                          }}
                          placeholder="İngilizce"
                          className="flex-1"
                        />
                        <Input
                          value={phrase.translation || ''}
                          onChange={(e) => {
                            const phrases = [...lessonContent.phrases];
                            phrases[idx] = { ...phrases[idx], translation: e.target.value };
                            setLessonContent({ ...lessonContent, phrases });
                          }}
                          placeholder="Türkçe"
                          className="flex-1"
                        />
                        <Button size="sm" variant="ghost" onClick={() => {
                          const phrases = lessonContent.phrases.filter((_, i) => i !== idx);
                          setLessonContent({ ...lessonContent, phrases });
                        }}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Quiz İçeriği */}
              {lessonContent.type === 'quiz' && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-gray-700">Sorular ({lessonContent.questions?.length || 0} adet)</label>
                    <Button size="sm" variant="outline" onClick={() => setLessonContent({
                      ...lessonContent,
                      questions: [...(lessonContent.questions || []), { question: '', options: ['', '', '', ''], correct: 0 }]
                    })}>
                      <Plus className="w-4 h-4 mr-1" /> Soru Ekle
                    </Button>
                  </div>
                  <div className="space-y-4 max-h-[400px] overflow-y-auto">
                    {(lessonContent.questions || []).map((q, idx) => (
                      <div key={idx} className="p-3 bg-gray-50 rounded border">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium">Soru {idx + 1}</span>
                          <Button size="sm" variant="ghost" onClick={() => {
                            const questions = lessonContent.questions.filter((_, i) => i !== idx);
                            setLessonContent({ ...lessonContent, questions });
                          }}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                        <Input
                          value={q.question || ''}
                          onChange={(e) => {
                            const questions = [...lessonContent.questions];
                            questions[idx] = { ...questions[idx], question: e.target.value };
                            setLessonContent({ ...lessonContent, questions });
                          }}
                          placeholder="Soru metni..."
                          className="mb-2"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          {(q.options || ['', '', '', '']).map((opt, optIdx) => (
                            <div key={optIdx} className="flex items-center gap-1">
                              <input
                                type="radio"
                                name={`correct-${idx}`}
                                checked={q.correct === optIdx}
                                onChange={() => {
                                  const questions = [...lessonContent.questions];
                                  questions[idx] = { ...questions[idx], correct: optIdx };
                                  setLessonContent({ ...lessonContent, questions });
                                }}
                                className="w-4 h-4"
                              />
                              <Input
                                value={opt}
                                onChange={(e) => {
                                  const questions = [...lessonContent.questions];
                                  const options = [...questions[idx].options];
                                  options[optIdx] = e.target.value;
                                  questions[idx] = { ...questions[idx], options };
                                  setLessonContent({ ...lessonContent, questions });
                                }}
                                placeholder={`Seçenek ${String.fromCharCode(65 + optIdx)}`}
                                className={q.correct === optIdx ? 'border-green-500 bg-green-50' : ''}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Diğer tipler için JSON düzenleme */}
              {!['flashcard', 'voice', 'quiz'].includes(lessonContent.type) && (
                <div>
                  <label className="text-sm font-medium text-gray-700">İçerik (JSON)</label>
                  <textarea
                    className="w-full h-64 border rounded-md p-3 font-mono text-sm mt-1"
                    value={JSON.stringify(lessonContent, null, 2)}
                    onChange={(e) => {
                      try {
                        const content = JSON.parse(e.target.value);
                        setLessonContent(content);
                      } catch (err) {
                        // Invalid JSON
                      }
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );

  // Edit Modal
  const renderEditModal = () => {
    if (!editMode) return null;
    
    const isModule = view === 'modules' || (view === 'units' && editData.module_id);
    const isUnit = view === 'units' && editData.unit_id !== undefined && !editData.module_id;
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <Card className="w-full max-w-lg">
          <CardHeader>
            <CardTitle>
              {isModule ? (editData.module_id ? 'Modül Düzenle' : 'Yeni Modül') : 
               isUnit ? (editData.unit_id ? 'Ünite Düzenle' : 'Yeni Ünite') :
               'Düzenle'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {isModule ? (
              <>
                <div>
                  <label className="text-sm font-medium">Modül ID</label>
                  <Input
                    value={editData.module_id || ''}
                    onChange={(e) => setEditData({ ...editData, module_id: e.target.value })}
                    placeholder="level_0"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Seviye Kodu</label>
                  <Input
                    value={editData.level_code || ''}
                    onChange={(e) => setEditData({ ...editData, level_code: e.target.value })}
                    placeholder="A0"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Başlık</label>
                  <Input
                    value={editData.title || ''}
                    onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Alt Başlık</label>
                  <Input
                    value={editData.subtitle || ''}
                    onChange={(e) => setEditData({ ...editData, subtitle: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">İkon (Emoji)</label>
                  <Input
                    value={editData.icon || ''}
                    onChange={(e) => setEditData({ ...editData, icon: e.target.value })}
                    placeholder="📚"
                  />
                </div>
              </>
            ) : isUnit ? (
              <>
                <div>
                  <label className="text-sm font-medium">Ünite ID</label>
                  <Input
                    value={editData.unit_id || ''}
                    onChange={(e) => setEditData({ ...editData, unit_id: e.target.value })}
                    placeholder="A0-U1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Başlık</label>
                  <Input
                    value={editData.title || ''}
                    onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">İkon (Emoji)</label>
                  <Input
                    value={editData.icon || ''}
                    onChange={(e) => setEditData({ ...editData, icon: e.target.value })}
                    placeholder="📖"
                  />
                </div>
              </>
            ) : null}
            
            <div className="flex gap-2 pt-4">
              <Button onClick={isModule ? handleSaveModule : handleSaveUnit} className="flex-1">
                <Save className="w-4 h-4 mr-1" /> Kaydet
              </Button>
              <Button variant="outline" onClick={() => { setEditMode(false); setEditData({}); }} className="flex-1">
                <X className="w-4 h-4 mr-1" /> İptal
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className={cn("text-2xl font-bold flex items-center gap-2", color)}>
          <TypeIcon className="w-6 h-6" />
          {title}
        </h2>
      </div>

      {renderDataSourceBanner()}
      {renderBreadcrumb()}

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-gray-500">Yükleniyor...</p>
        </div>
      ) : (
        <>
          {view === 'modules' && renderModulesView()}
          {view === 'units' && renderUnitsView()}
          {view === 'lessons' && renderLessonsView()}
          {view === 'lesson-edit' && renderLessonEditView()}
        </>
      )}
      
      {renderEditModal()}
    </div>
  );
}

// ==================== LEVEL TEST MANAGEMENT ====================
export function LevelTestManagement() {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [dataSource, setDataSource] = useState('unknown');
  const [importing, setImporting] = useState(false);
  const [newQuestion, setNewQuestion] = useState({
    question: '',
    type: 'multipleChoice',
    options: ['', '', '', ''],
    correct_answer: 0,
    level: 'A1',
    category: 'grammar',
    points: 10
  });

  const fetchQuestions = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/education/level-test`, { withCredentials: true });
      setQuestions(res.data.questions || []);
      setDataSource(res.data.source || 'unknown');
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const importToDatabase = async () => {
    if (!window.confirm('Static soruları veritabanına aktarmak istiyor musunuz?')) return;
    setImporting(true);
    try {
      await axios.post(`${API}/api/admin/education/level-test/import`, {}, { withCredentials: true });
      alert('Sorular veritabanına aktarıldı!');
      fetchQuestions();
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.detail || err.message));
    } finally {
      setImporting(false);
    }
  };

  useEffect(() => {
    fetchQuestions();
  }, []);

  const handleSave = async () => {
    try {
      const data = editingQuestion || newQuestion;
      if (editingQuestion) {
        await axios.put(`${API}/api/admin/education/level-test/${editingQuestion.id}`, data, { withCredentials: true });
      } else {
        await axios.post(`${API}/api/admin/education/level-test`, data, { withCredentials: true });
      }
      fetchQuestions();
      setEditingQuestion(null);
      setShowAddForm(false);
      setNewQuestion({ question: '', type: 'multipleChoice', options: ['', '', '', ''], correct_answer: 0, level: 'A0', points: 10 });
      alert('Kaydedildi!');
    } catch (err) {
      alert('Hata: ' + err.message);
    }
  };

  const handleDelete = async (questionId) => {
    if (!window.confirm('Bu soruyu silmek istediğinizden emin misiniz?')) return;
    try {
      await axios.delete(`${API}/api/admin/education/level-test/${questionId}`, { withCredentials: true });
      fetchQuestions();
    } catch (err) {
      alert('Hata: ' + err.message);
    }
  };

  const QuestionForm = ({ data, setData, onSave, onCancel }) => (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="text-lg">
          {editingQuestion ? 'Soru Düzenle' : 'Yeni Soru Ekle'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <label className="text-sm font-medium">Soru</label>
          <Input
            value={data.question}
            onChange={(e) => setData({ ...data, question: e.target.value })}
            placeholder="Soru metnini girin..."
          />
        </div>
        
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="text-sm font-medium">Seviye</label>
            <select
              className="w-full border rounded-md p-2"
              value={data.level}
              onChange={(e) => setData({ ...data, level: e.target.value })}
            >
              <option value="A1">A1</option>
              <option value="A2">A2</option>
              <option value="B1">B1</option>
              <option value="B2">B2</option>
              <option value="C1">C1</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-medium">Kategori</label>
            <select
              className="w-full border rounded-md p-2"
              value={data.category || 'grammar'}
              onChange={(e) => setData({ ...data, category: e.target.value })}
            >
              <option value="grammar">Gramer</option>
              <option value="vocabulary">Kelime</option>
              <option value="reading">Okuma</option>
              <option value="listening">Dinleme</option>
            </select>
          </div>
          <div>
            <label className="text-sm font-medium">Puan</label>
            <Input
              type="number"
              value={data.points || 10}
              onChange={(e) => setData({ ...data, points: parseInt(e.target.value) })}
            />
          </div>
        </div>
        
        <div>
          <label className="text-sm font-medium">Seçenekler (Doğru cevabı işaretleyin)</label>
          <div className="space-y-2 mt-2">
            {data.options.map((opt, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <input
                  type="radio"
                  name="correct"
                  checked={data.correct_answer === idx}
                  onChange={() => setData({ ...data, correct_answer: idx })}
                  className="w-4 h-4"
                />
                <Input
                  value={opt}
                  onChange={(e) => {
                    const opts = [...data.options];
                    opts[idx] = e.target.value;
                    setData({ ...data, options: opts });
                  }}
                  placeholder={`Seçenek ${idx + 1}`}
                  className={data.correct_answer === idx ? 'border-green-500 bg-green-50' : ''}
                />
              </div>
            ))}
          </div>
        </div>
        
        <div className="flex gap-2 pt-4">
          <Button onClick={onSave}>
            <Save className="w-4 h-4 mr-1" /> Kaydet
          </Button>
          <Button variant="outline" onClick={onCancel}>
            <X className="w-4 h-4 mr-1" /> İptal
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="p-6" data-testid="level-test-management">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">📝 Seviye Tespit Sınavı</h2>
        <div className="flex gap-2">
          {dataSource === 'static' && (
            <Button onClick={importToDatabase} disabled={importing} className="bg-blue-600 hover:bg-blue-700">
              {importing ? (
                <>
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                  Aktarılıyor...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-1" /> Veritabanına Aktar
                </>
              )}
            </Button>
          )}
          {!showAddForm && !editingQuestion && (
            <Button onClick={() => setShowAddForm(true)}>
              <Plus className="w-4 h-4 mr-1" /> Yeni Soru
            </Button>
          )}
        </div>
      </div>

      {/* Data Source Banner */}
      <div className={cn(
        "p-3 rounded-lg mb-4 flex items-center gap-2",
        dataSource === 'database' ? "bg-green-50 border border-green-200" : "bg-yellow-50 border border-yellow-200"
      )}>
        {dataSource === 'database' ? (
          <>
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <span className="text-sm text-green-700">✅ Veritabanından okunuyor - Düzenleme aktif ({questions.length} soru)</span>
          </>
        ) : (
          <>
            <div className="w-2 h-2 bg-yellow-500 rounded-full" />
            <span className="text-sm text-yellow-700">⚠️ Static dosyadan okunuyor ({questions.length} soru) - Düzenleme için veritabanına aktarın</span>
          </>
        )}
      </div>
      
      {(showAddForm || editingQuestion) && (
        <QuestionForm
          data={editingQuestion || newQuestion}
          setData={editingQuestion ? setEditingQuestion : setNewQuestion}
          onSave={handleSave}
          onCancel={() => { setEditingQuestion(null); setShowAddForm(false); }}
        />
      )}
      
      {/* Questions List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Sorular ({questions.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-gray-500">Yükleniyor...</p>
          ) : questions.length === 0 ? (
            <p className="text-gray-500 text-center py-8">Henüz soru yok.</p>
          ) : (
            <div className="space-y-3">
              {questions.map((q, idx) => (
                <div key={q.id || idx} className="p-4 border rounded-lg hover:bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-bold bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                          {q.level}
                        </span>
                        <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">
                          {q.points} puan
                        </span>
                      </div>
                      <p className="font-medium mb-2">{idx + 1}. {q.question}</p>
                      <div className="flex flex-wrap gap-2">
                        {q.options?.map((opt, optIdx) => (
                          <span
                            key={optIdx}
                            className={cn(
                              "text-sm px-3 py-1 rounded-full",
                              q.correct_answer === optIdx 
                                ? "bg-green-100 text-green-700 font-medium" 
                                : "bg-gray-100 text-gray-600"
                            )}
                          >
                            {q.correct_answer === optIdx && '✓ '}{opt}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-1 ml-4">
                      <Button size="sm" variant="ghost" onClick={() => setEditingQuestion(q)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleDelete(q.id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== SCORING MANAGEMENT ====================
export function ScoringManagement() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [milesAmount, setXpAmount] = useState(100);

  const fetchStudents = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/students?limit=200`, { withCredentials: true });
      setStudents(res.data.students || []);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStudents();
  }, []);

  const handleBulkMiles = async () => {
    if (selectedStudents.length === 0) {
      alert('Lütfen en az bir öğrenci seçin');
      return;
    }
    try {
      await axios.post(`${API}/api/admin/bulk/update-xp`, {
        user_ids: selectedStudents,
        xp_change: milesAmount
      }, { withCredentials: true });
      fetchStudents();
      setSelectedStudents([]);
      alert(`${selectedStudents.length} öğrenciye ${milesAmount > 0 ? '+' : ''}${milesAmount} Mil uygulandı!`);
    } catch (err) {
      alert('Hata: ' + err.message);
    }
  };

  const toggleStudent = (studentId) => {
    if (selectedStudents.includes(studentId)) {
      setSelectedStudents(selectedStudents.filter(id => id !== studentId));
    } else {
      setSelectedStudents([...selectedStudents, studentId]);
    }
  };

  const selectAll = () => {
    if (selectedStudents.length === students.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(students.map(s => s.id));
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">🏆 Puanlama Yönetimi</h2>
      
      {/* Toplu Mil */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg">Toplu Mil İşlemi</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="text-sm font-medium block mb-1">Mil Miktarı</label>
              <Input
                type="number"
                value={milesAmount}
                onChange={(e) => setXpAmount(parseInt(e.target.value) || 0)}
                className="w-32"
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setXpAmount(50)}>+50</Button>
              <Button variant="outline" onClick={() => setXpAmount(100)}>+100</Button>
              <Button variant="outline" onClick={() => setXpAmount(500)}>+500</Button>
              <Button variant="outline" onClick={() => setXpAmount(-100)}>-100</Button>
            </div>
            <Button 
              onClick={handleBulkMiles} 
              disabled={selectedStudents.length === 0}
              className={milesAmount >= 0 ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
            >
              <Award className="w-4 h-4 mr-1" />
              {selectedStudents.length} Öğrenciye {milesAmount >= 0 ? 'Ekle' : 'Düş'}
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Student List */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">
            Öğrenciler 
            {selectedStudents.length > 0 && (
              <span className="ml-2 text-sm font-normal text-blue-600">
                ({selectedStudents.length} seçili)
              </span>
            )}
          </CardTitle>
          <Button size="sm" variant="outline" onClick={selectAll}>
            {selectedStudents.length === students.length ? 'Seçimi Kaldır' : 'Tümünü Seç'}
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-gray-500">Yükleniyor...</p>
          ) : (
            <div className="max-h-[500px] overflow-y-auto">
              <table className="w-full">
                <thead className="sticky top-0 bg-white border-b">
                  <tr>
                    <th className="p-3 text-left w-10">
                      <input
                        type="checkbox"
                        checked={selectedStudents.length === students.length && students.length > 0}
                        onChange={selectAll}
                        className="w-4 h-4"
                      />
                    </th>
                    <th className="p-3 text-left">İsim</th>
                    <th className="p-3 text-left">Email</th>
                    <th className="p-3 text-right"> Mil</th>
                    <th className="p-3 text-center">Seviye</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map(student => (
                    <tr 
                      key={student.id} 
                      className={cn(
                        "border-b hover:bg-gray-50 cursor-pointer",
                        selectedStudents.includes(student.id) && "bg-blue-50"
                      )}
                      onClick={() => toggleStudent(student.id)}
                    >
                      <td className="p-3">
                        <input
                          type="checkbox"
                          checked={selectedStudents.includes(student.id)}
                          onChange={() => toggleStudent(student.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="w-4 h-4"
                        />
                      </td>
                      <td className="p-3 font-medium">{student.name || 'İsimsiz'}</td>
                      <td className="p-3 text-gray-500">{student.email}</td>
                      <td className="p-3 text-right">
                        <span className="text-yellow-600 font-bold">{student.xp || 0}</span>
                      </td>
                      <td className="p-3 text-center">
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                          {student.current_level || 'A0'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== SKILL TESTS MANAGEMENT (Speaking, Writing, Listening) ====================
export function SkillTestManagement() {
  const [activeTab, setActiveTab] = useState('speaking');
  const [speakingQuestions, setSpeakingQuestions] = useState([]);
  const [listeningQuestions, setListeningQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState('all');
  
  const [newQuestion, setNewQuestion] = useState({
    level: 'A1',
    category: 'interview',
    question: '',
    question_tr: '',
    tips: '',
    expected_answer: '',
    audio_url: '',
    audio_text: '',
    options: ['', '', '', ''],
    correct_answer: 0,
    xp: 30,
    duration: 60
  });

  const tabs = [
    { id: 'speaking', label: 'Speaking', icon: '🎤', color: 'pink' },
    { id: 'listening', label: 'Listening', icon: '🎧', color: 'green' }
  ];

  const levels = ['A0', 'A1', 'A2', 'B1', 'B2', 'B2-INT', 'C1', 'C1-INT', 'C2'];
  const categories = {
    speaking: ['interview', 'introduction', 'scenario', 'experience', 'competency', 'teamwork', 'general', 'industry'],
    listening: ['announcement', 'dialogue', 'comprehension', 'instruction', 'conversation', 'emergency']
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [speakingRes, listeningRes] = await Promise.all([
        axios.get(`${API}/api/admin/speaking-questions`, { withCredentials: true }),
        axios.get(`${API}/api/admin/listening-questions`, { withCredentials: true })
      ]);
      setSpeakingQuestions(speakingRes.data.questions || []);
      setListeningQuestions(listeningRes.data.questions || []);
    } catch (error) {
      console.error('Error fetching skill data:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const resetForm = () => {
    setNewQuestion({
      level: 'A1',
      category: categories[activeTab][0],
      question: '',
      question_tr: '',
      tips: '',
      expected_answer: '',
      audio_url: '',
      audio_text: '',
      options: ['', '', '', ''],
      correct_answer: 0,
      xp: 30,
      duration: 60
    });
    setEditingQuestion(null);
    setShowAddForm(false);
  };

  const handleSaveQuestion = async () => {
    try {
      const endpoint = activeTab === 'speaking' ? 'speaking-questions' : 'listening-questions';
      
      if (editingQuestion) {
        await axios.put(`${API}/api/admin/${endpoint}/${editingQuestion.id}`, newQuestion, { withCredentials: true });
        alert('Soru güncellendi!');
      } else {
        await axios.post(`${API}/api/admin/${endpoint}`, newQuestion, { withCredentials: true });
        alert('Soru eklendi!');
      }
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving question:', error);
      alert('Hata: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEditQuestion = (question) => {
    setNewQuestion({
      level: question.level || 'A1',
      category: question.category || categories[activeTab][0],
      question: question.question || '',
      question_tr: question.question_tr || '',
      tips: question.tips || '',
      expected_answer: question.expected_answer || '',
      audio_url: question.audio_url || '',
      audio_text: question.audio_text || '',
      options: question.options || ['', '', '', ''],
      correct_answer: question.correct_answer || 0,
      xp: question.xp || 30,
      duration: question.duration || 60
    });
    setEditingQuestion(question);
    setShowAddForm(true);
  };

  const handleDeleteQuestion = async (questionId) => {
    if (!window.confirm('Bu soruyu silmek istediğinize emin misiniz?')) return;
    try {
      const endpoint = activeTab === 'speaking' ? 'speaking-questions' : 'listening-questions';
      await axios.delete(`${API}/api/admin/${endpoint}/${questionId}`, { withCredentials: true });
      alert('Soru silindi!');
      fetchData();
    } catch (error) {
      console.error('Error deleting question:', error);
      alert('Hata: ' + (error.response?.data?.detail || error.message));
    }
  };

  const currentQuestions = activeTab === 'speaking' ? speakingQuestions : listeningQuestions;
  const filteredQuestions = selectedLevel === 'all' 
    ? currentQuestions 
    : currentQuestions.filter(q => q.level === selectedLevel);

  // Seviyeye göre grupla
  const groupedByLevel = currentQuestions.reduce((acc, q) => {
    const level = q.level || 'Unknown';
    if (!acc[level]) acc[level] = [];
    acc[level].push(q);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="skill-test-management">
      {/* Tab Navigation */}
      <div className="flex gap-2">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => { setActiveTab(tab.id); setSelectedLevel('all'); }}
            data-testid={`tab-${tab.id}`}
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors",
              activeTab === tab.id 
                ? "border-2"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            )}
            style={{
              backgroundColor: activeTab === tab.id ? 
                (tab.color === 'pink' ? '#fce7f3' : '#dcfce7') : undefined,
              color: activeTab === tab.id ?
                (tab.color === 'pink' ? '#be185d' : '#15803d') : undefined,
              borderColor: activeTab === tab.id ?
                (tab.color === 'pink' ? '#f9a8d4' : '#86efac') : undefined
            }}
          >
            <span className="text-xl">{tab.icon}</span>
            <span>{tab.label}</span>
            <span className="text-xs bg-white px-2 py-0.5 rounded-full font-bold">
              {activeTab === tab.id ? currentQuestions.length : (tab.id === 'speaking' ? speakingQuestions.length : listeningQuestions.length)}
            </span>
          </button>
        ))}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-pink-100 rounded-lg text-2xl">🎤</div>
              <div>
                <p className="text-sm text-gray-500">Speaking</p>
                <p className="text-2xl font-bold">{speakingQuestions.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg text-2xl">🎧</div>
              <div>
                <p className="text-sm text-gray-500">Listening</p>
                <p className="text-2xl font-bold">{listeningQuestions.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg text-2xl">📊</div>
              <div>
                <p className="text-sm text-gray-500">Seviye Sayısı</p>
                <p className="text-2xl font-bold">{Object.keys(groupedByLevel).length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg text-2xl">⭐</div>
              <div>
                <p className="text-sm text-gray-500">Toplam</p>
                <p className="text-2xl font-bold">{speakingQuestions.length + listeningQuestions.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold capitalize">{activeTab} Soruları</h3>
          <select
            value={selectedLevel}
            onChange={(e) => setSelectedLevel(e.target.value)}
            className="p-2 border rounded-lg text-sm"
            data-testid="level-filter"
          >
            <option value="all">Tüm Seviyeler</option>
            {levels.map(l => (
              <option key={l} value={l}>{l} ({groupedByLevel[l]?.length || 0})</option>
            ))}
          </select>
        </div>
        <Button 
          onClick={() => { resetForm(); setShowAddForm(true); }} 
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="add-question-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> Yeni Soru Ekle
        </Button>
      </div>

      {/* Add/Edit Question Form */}
      {showAddForm && (
        <Card className="border-2 border-blue-200">
          <CardHeader>
            <CardTitle className="text-lg">
              {editingQuestion ? 'Soruyu Düzenle' : `Yeni ${activeTab === 'speaking' ? 'Speaking' : 'Listening'} Sorusu`}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="text-sm font-medium">Seviye</label>
                <select
                  value={newQuestion.level}
                  onChange={(e) => setNewQuestion({...newQuestion, level: e.target.value})}
                  className="w-full p-2 border rounded-lg mt-1"
                  data-testid="question-level"
                >
                  {levels.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Kategori</label>
                <select
                  value={newQuestion.category}
                  onChange={(e) => setNewQuestion({...newQuestion, category: e.target.value})}
                  className="w-full p-2 border rounded-lg mt-1"
                  data-testid="question-category"
                >
                  {categories[activeTab].map(c => (
                    <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Mil (XP)</label>
                <Input
                  type="number"
                  value={newQuestion.xp}
                  onChange={(e) => setNewQuestion({...newQuestion, xp: parseInt(e.target.value) || 0})}
                  className="mt-1"
                  data-testid="question-xp"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Süre (sn)</label>
                <Input
                  type="number"
                  value={newQuestion.duration}
                  onChange={(e) => setNewQuestion({...newQuestion, duration: parseInt(e.target.value) || 60})}
                  className="mt-1"
                  data-testid="question-duration"
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium">Soru (İngilizce) *</label>
              <textarea
                value={newQuestion.question}
                onChange={(e) => setNewQuestion({...newQuestion, question: e.target.value})}
                className="w-full p-2 border rounded-lg mt-1 min-h-[80px]"
                placeholder="Enter the question in English..."
                data-testid="question-text"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">Soru (Türkçe)</label>
              <textarea
                value={newQuestion.question_tr}
                onChange={(e) => setNewQuestion({...newQuestion, question_tr: e.target.value})}
                className="w-full p-2 border rounded-lg mt-1 min-h-[60px]"
                placeholder="Soruyu Türkçe yazın..."
                data-testid="question-text-tr"
              />
            </div>

            {activeTab === 'speaking' && (
              <>
                <div>
                  <label className="text-sm font-medium">İpuçları / Talimatlar</label>
                  <textarea
                    value={newQuestion.tips}
                    onChange={(e) => setNewQuestion({...newQuestion, tips: e.target.value})}
                    className="w-full p-2 border rounded-lg mt-1"
                    placeholder="Öğrenciye gösterilecek ipuçları..."
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Beklenen Cevap / Değerlendirme Kriterleri</label>
                  <textarea
                    value={newQuestion.expected_answer}
                    onChange={(e) => setNewQuestion({...newQuestion, expected_answer: e.target.value})}
                    className="w-full p-2 border rounded-lg mt-1"
                    placeholder="Model cevap veya değerlendirme kriterleri..."
                  />
                </div>
              </>
            )}

            {activeTab === 'listening' && (
              <>
                <div>
                  <label className="text-sm font-medium">Audio URL</label>
                  <Input
                    value={newQuestion.audio_url}
                    onChange={(e) => setNewQuestion({...newQuestion, audio_url: e.target.value})}
                    className="mt-1"
                    placeholder="/audio/a1_example.mp3"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Audio Transkript</label>
                  <textarea
                    value={newQuestion.audio_text}
                    onChange={(e) => setNewQuestion({...newQuestion, audio_text: e.target.value})}
                    className="w-full p-2 border rounded-lg mt-1"
                    placeholder="Ses dosyasının transkripti..."
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Seçenekler (Çoktan Seçmeli için)</label>
                  <div className="space-y-2 mt-2">
                    {newQuestion.options.map((opt, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="correct"
                          checked={newQuestion.correct_answer === idx}
                          onChange={() => setNewQuestion({...newQuestion, correct_answer: idx})}
                          className="w-4 h-4"
                        />
                        <Input
                          value={opt}
                          onChange={(e) => {
                            const opts = [...newQuestion.options];
                            opts[idx] = e.target.value;
                            setNewQuestion({...newQuestion, options: opts});
                          }}
                          placeholder={`Seçenek ${String.fromCharCode(65 + idx)}`}
                          className={newQuestion.correct_answer === idx ? 'border-green-500 bg-green-50' : ''}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
            
            <div className="flex gap-2 pt-4">
              <Button onClick={handleSaveQuestion} className="bg-green-600 hover:bg-green-700" data-testid="save-question-btn">
                <Save className="w-4 h-4 mr-1" /> {editingQuestion ? 'Güncelle' : 'Kaydet'}
              </Button>
              <Button variant="outline" onClick={resetForm} data-testid="cancel-question-btn">
                <X className="w-4 h-4 mr-1" /> İptal
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Questions List */}
      <Card>
        <CardContent className="p-4">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-gray-500">Yükleniyor...</p>
            </div>
          ) : filteredQuestions.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 mb-4">
                {selectedLevel === 'all' 
                  ? `Henüz ${activeTab} sorusu eklenmemiş.`
                  : `${selectedLevel} seviyesinde soru bulunamadı.`}
              </p>
              <Button onClick={() => { resetForm(); setShowAddForm(true); }}>
                <Plus className="w-4 h-4 mr-1" /> İlk Soruyu Ekle
              </Button>
            </div>
          ) : (
            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              {filteredQuestions.map((question, idx) => (
                <div 
                  key={question.id || idx}
                  className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors border"
                  data-testid={`question-item-${question.id || idx}`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className="text-xs font-bold bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                          {question.level}
                        </span>
                        {question.category && (
                          <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded">
                            {question.category}
                          </span>
                        )}
                        <span className="text-xs text-yellow-600 font-medium">
                          +{question.xp || 30} Mil
                        </span>
                        {question.duration && (
                          <span className="text-xs text-gray-500">
                            {question.duration}s
                          </span>
                        )}
                      </div>
                      <p className="font-medium text-gray-800">{question.question}</p>
                      {question.question_tr && (
                        <p className="text-sm text-gray-500 mt-1 italic">{question.question_tr}</p>
                      )}
                      {activeTab === 'listening' && question.audio_url && (
                        <p className="text-xs text-blue-500 mt-1">🎵 {question.audio_url}</p>
                      )}
                    </div>
                    <div className="flex gap-1 ml-4">
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => handleEditQuestion(question)}
                        data-testid={`edit-question-${question.id || idx}`}
                      >
                        <Edit className="w-4 h-4 text-blue-500" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => handleDeleteQuestion(question.id)}
                        data-testid={`delete-question-${question.id || idx}`}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default {
  StudentManagement,
  ContentManagement,
  LevelTestManagement,
  ScoringManagement,
  SkillTestManagement,
  EducationProgramsManagement,
  BulkCertificatesManagement
};

// ==================== EĞİTİM PROGRAMLARI YÖNETİMİ ====================
export function EducationProgramsManagement() {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [formData, setFormData] = useState({ program_name: '', duration_hours: '', description: '' });

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    loadPrograms();
  }, []);

  const loadPrograms = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/education-programs`, { withCredentials: true });
      setPrograms(res.data.programs || []);
    } catch (err) {
      console.error('Error loading programs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProgram) {
        await axios.put(`${API}/admin/education-programs/${editingProgram.id}`, formData, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/education-programs`, formData, { withCredentials: true });
      }
      loadPrograms();
      resetForm();
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu programı silmek istediğinizden emin misiniz?')) return;
    try {
      await axios.delete(`${API}/admin/education-programs/${id}`, { withCredentials: true });
      loadPrograms();
    } catch (err) {
      alert('Silme hatası: ' + (err.response?.data?.detail || err.message));
    }
  };

  const resetForm = () => {
    setFormData({ program_name: '', duration_hours: '', description: '' });
    setEditingProgram(null);
    setShowForm(false);
  };

  const startEdit = (program) => {
    setFormData({
      program_name: program.program_name,
      duration_hours: program.duration_hours,
      description: program.description || ''
    });
    setEditingProgram(program);
    setShowForm(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Eğitim Programları</h3>
          <p className="text-sm text-gray-500">Sertifika basımı için eğitim programlarını yönetin</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-amber-600 hover:bg-amber-700">
          <Plus className="w-4 h-4 mr-2" /> Yeni Program
        </Button>
      </div>

      {/* Form */}
      {showForm && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Program Adı *</label>
                  <Input
                    value={formData.program_name}
                    onChange={(e) => setFormData({ ...formData, program_name: e.target.value })}
                    placeholder="Örn: Kabin Memurluğu Eğitimi"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Eğitim Saati *</label>
                  <Input
                    type="number"
                    value={formData.duration_hours}
                    onChange={(e) => setFormData({ ...formData, duration_hours: e.target.value })}
                    placeholder="Örn: 120"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Açıklama</label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Programa ait açıklama..."
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit" className="bg-amber-600 hover:bg-amber-700">
                  <Save className="w-4 h-4 mr-1" /> {editingProgram ? 'Güncelle' : 'Kaydet'}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  <X className="w-4 h-4 mr-1" /> İptal
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Programs List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full mx-auto"></div>
            </div>
          ) : programs.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Henüz eğitim programı eklenmemiş.
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left p-4 text-sm font-medium text-gray-600">Program Adı</th>
                  <th className="text-left p-4 text-sm font-medium text-gray-600">Eğitim Saati</th>
                  <th className="text-left p-4 text-sm font-medium text-gray-600">Açıklama</th>
                  <th className="text-right p-4 text-sm font-medium text-gray-600">İşlemler</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {programs.map((program) => (
                  <tr key={program.id} className="hover:bg-gray-50">
                    <td className="p-4 font-medium text-gray-800">{program.program_name}</td>
                    <td className="p-4 text-gray-600">{program.duration_hours} Saat</td>
                    <td className="p-4 text-gray-500 text-sm">{program.description || '-'}</td>
                    <td className="p-4 text-right">
                      <Button variant="ghost" size="sm" onClick={() => startEdit(program)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(program.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== TOPLU SERTİFİKA BASIMI ====================
export function BulkCertificatesManagement() {
  const [programs, setPrograms] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [programsRes, certsRes] = await Promise.all([
        axios.get(`${API}/admin/education-programs`, { withCredentials: true }),
        axios.get(`${API}/admin/certificates/bulk-list`, { withCredentials: true })
      ]);
      setPrograms(programsRes.data.programs || []);
      setCertificates(certsRes.data.certificates || []);
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (!file || !selectedProgram) {
      alert('Lütfen bir eğitim programı seçin ve Excel dosyası yükleyin');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('program_id', selectedProgram);

      const res = await axios.post(`${API}/admin/certificates/bulk-generate`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      alert(`${res.data.total_processed} sertifika kaydı oluşturuldu!`);
      if (res.data.errors?.length > 0) {
        console.log('Hatalar:', res.data.errors);
      }
      setFile(null);
      loadData();
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu sertifika kaydını silmek istediğinizden emin misiniz?')) return;
    try {
      await axios.delete(`${API}/admin/certificates/bulk/${id}`, { withCredentials: true });
      loadData();
    } catch (err) {
      alert('Silme hatası');
    }
  };

  const downloadTemplate = () => {
    // Basit bir şablon örneği için blob oluştur
    const headers = "Ad Soyad\tTC Kimlik No\tTarih\n";
    const example = "Ahmet Yılmaz\t12345678901\t15.01.2025\nMehmet Demir\t98765432109\t15.01.2025";
    const content = headers + example;
    
    const blob = new Blob([content], { type: 'text/tab-separated-values' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sertifika_sablon.tsv';
    a.click();
  };

  return (
    <div className="space-y-6">
      {/* Upload Section */}
      <Card className="border-amber-200">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Excel'den Toplu Sertifika Yükle</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Eğitim Programı *</label>
              <select
                value={selectedProgram}
                onChange={(e) => setSelectedProgram(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              >
                <option value="">Seçiniz...</option>
                {programs.map((p) => (
                  <option key={p.id} value={p.id}>{p.program_name} ({p.duration_hours} Saat)</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Excel Dosyası *</label>
              <Input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="cursor-pointer"
              />
            </div>
            <div className="flex items-end gap-2">
              <Button onClick={handleUpload} disabled={uploading || !file || !selectedProgram} className="bg-amber-600 hover:bg-amber-700">
                {uploading ? (
                  <>
                    <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                    Yükleniyor...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" /> Yükle
                  </>
                )}
              </Button>
              <Button variant="outline" onClick={downloadTemplate}>
                <FileSpreadsheet className="w-4 h-4 mr-2" /> Şablon İndir
              </Button>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
            <strong>Excel Formatı:</strong> İlk satır başlık olmalı. Sütunlar: Ad Soyad (zorunlu), TC Kimlik No (opsiyonel), Tarih (opsiyonel - boşsa bugün)
          </div>
        </CardContent>
      </Card>

      {/* Certificates List */}
      <Card>
        <CardContent className="p-0">
          <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
            <h3 className="font-semibold text-gray-800">Sertifika Kayıtları ({certificates.length})</h3>
          </div>
          
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full mx-auto"></div>
            </div>
          ) : certificates.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Henüz sertifika kaydı yok. Excel dosyası yükleyerek toplu sertifika oluşturabilirsiniz.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Sertifika No</th>
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Ad Soyad</th>
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Program</th>
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Süre</th>
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Tarih</th>
                    <th className="text-left p-3 text-sm font-medium text-gray-600">Durum</th>
                    <th className="text-right p-3 text-sm font-medium text-gray-600">İşlem</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {certificates.map((cert) => (
                    <tr key={cert.id} className="hover:bg-gray-50">
                      <td className="p-3 font-mono text-sm text-gray-600">{cert.certificate_no}</td>
                      <td className="p-3 font-medium text-gray-800">{cert.full_name}</td>
                      <td className="p-3 text-gray-600">{cert.program_name}</td>
                      <td className="p-3 text-gray-600">{cert.duration_hours} Saat</td>
                      <td className="p-3 text-gray-600">{cert.issue_date}</td>
                      <td className="p-3">
                        <span className={cn(
                          "px-2 py-1 rounded-full text-xs font-medium",
                          cert.status === 'pending' && "bg-yellow-100 text-yellow-700",
                          cert.status === 'generated' && "bg-green-100 text-green-700",
                          cert.status === 'sent' && "bg-blue-100 text-blue-700"
                        )}>
                          {cert.status === 'pending' ? 'Bekliyor' : cert.status === 'generated' ? 'Oluşturuldu' : 'Gönderildi'}
                        </span>
                      </td>
                      <td className="p-3 text-right">
                        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDelete(cert.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== SMS REMINDER MANAGEMENT ====================
export function SmsReminderManagement() {
  const [settings, setSettings] = useState({
    enabled: true,
    reminder_days: [7, 14, 30],
    message_template: ''
  });
  const [stats, setStats] = useState(null);
  const [inactiveStudents, setInactiveStudents] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [activeTab, setActiveTab] = useState('settings');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [settingsRes, statsRes, inactiveRes] = await Promise.all([
        axios.get(`${API}/api/admin/settings/sms-reminder`, { withCredentials: true }),
        axios.get(`${API}/api/admin/sms-reminder/stats`, { withCredentials: true }),
        axios.get(`${API}/api/admin/sms-reminder/inactive-students`, { withCredentials: true })
      ]);
      setSettings(settingsRes.data);
      setStats(statsRes.data);
      setInactiveStudents(inactiveRes.data);
    } catch (err) {
      console.error('Error fetching SMS reminder data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/settings/sms-reminder`, settings, { withCredentials: true });
      alert('Ayarlar kaydedildi!');
    } catch (err) {
      alert('Kaydetme hatası: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRunNow = async () => {
    if (!window.confirm('SMS hatırlatmalarını şimdi göndermek istediğinizden emin misiniz?')) return;
    setRunning(true);
    try {
      const res = await axios.post(`${API}/api/admin/sms-reminder/run-now`, {}, { withCredentials: true });
      alert(`İşlem tamamlandı!\nGönderilen: ${res.data.sent}\nBaşarısız: ${res.data.failed}\nAtlanan: ${res.data.skipped}`);
      fetchData();
    } catch (err) {
      alert('Hata: ' + err.message);
    } finally {
      setRunning(false);
    }
  };

  const toggleDay = (day) => {
    const days = settings.reminder_days || [];
    if (days.includes(day)) {
      setSettings({ ...settings, reminder_days: days.filter(d => d !== day) });
    } else {
      setSettings({ ...settings, reminder_days: [...days, day].sort((a, b) => a - b) });
    }
  };

  if (loading) {
    return <div className="p-6 text-center text-gray-500">Yükleniyor...</div>;
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">📱 SMS Hatırlatma Sistemi</h2>
        <div className="flex gap-2">
          <Button 
            onClick={handleRunNow} 
            disabled={running || !settings.enabled}
            className="bg-green-600 hover:bg-green-700"
          >
            {running ? 'Gönderiliyor...' : '🚀 Şimdi Gönder'}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <Button 
          variant={activeTab === 'settings' ? 'default' : 'outline'} 
          onClick={() => setActiveTab('settings')}
        >
          ⚙️ Ayarlar
        </Button>
        <Button 
          variant={activeTab === 'stats' ? 'default' : 'outline'} 
          onClick={() => setActiveTab('stats')}
        >
          📊 İstatistikler
        </Button>
        <Button 
          variant={activeTab === 'students' ? 'default' : 'outline'} 
          onClick={() => setActiveTab('students')}
        >
          👥 İnaktif Öğrenciler
        </Button>
      </div>

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="space-y-6">
          {/* Enable/Disable */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Sistem Durumu</CardTitle>
            </CardHeader>
            <CardContent>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.enabled}
                  onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
                  className="w-5 h-5"
                />
                <span className={cn(
                  "font-medium",
                  settings.enabled ? "text-green-600" : "text-red-600"
                )}>
                  {settings.enabled ? '✅ SMS Hatırlatma Aktif' : '❌ SMS Hatırlatma Devre Dışı'}
                </span>
              </label>
            </CardContent>
          </Card>

          {/* Reminder Days */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Hatırlatma Günleri</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-500 mb-4">
                Öğrenci bu kadar gün giriş yapmadığında SMS gönderilir
              </p>
              <div className="flex gap-3">
                {[7, 14, 21, 30, 45, 60].map(day => (
                  <button
                    key={day}
                    onClick={() => toggleDay(day)}
                    className={cn(
                      "px-4 py-2 rounded-lg font-medium transition-colors",
                      (settings.reminder_days || []).includes(day)
                        ? "bg-blue-600 text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    )}
                  >
                    {day} Gün
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Message Template */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">SMS Mesaj Şablonu</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-500 mb-2">
                <code className="bg-gray-100 px-1 rounded">{'{name}'}</code> öğrenci adı ile değiştirilir
              </p>
              <textarea
                value={settings.message_template || ''}
                onChange={(e) => setSettings({ ...settings, message_template: e.target.value })}
                className="w-full border rounded-lg p-3 min-h-[100px]"
                placeholder="Merhaba {name}! 🛫 Seni özledik..."
              />
              <p className="text-sm text-gray-400 mt-2">
                Karakter sayısı: {(settings.message_template || '').length} / 160
              </p>
            </CardContent>
          </Card>

          <Button onClick={handleSave} disabled={saving} className="w-full">
            {saving ? 'Kaydediliyor...' : '💾 Ayarları Kaydet'}
          </Button>
        </div>
      )}

      {/* Stats Tab */}
      {activeTab === 'stats' && stats && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
              <CardContent className="p-4">
                <p className="text-blue-100 text-sm">Son 30 Gün Gönderilen</p>
                <p className="text-3xl font-bold">{stats.total_sent_30d}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
              <CardContent className="p-4">
                <p className="text-red-100 text-sm">Başarısız</p>
                <p className="text-3xl font-bold">{stats.total_failed_30d}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
              <CardContent className="p-4">
                <p className="text-green-100 text-sm">Geri Dönen Öğrenci</p>
                <p className="text-3xl font-bold">{stats.returned_students}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
              <CardContent className="p-4">
                <p className="text-purple-100 text-sm">Dönüş Oranı</p>
                <p className="text-3xl font-bold">%{stats.return_rate}</p>
              </CardContent>
            </Card>
          </div>

          {/* Recent Logs */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Son Gönderilen SMS'ler</CardTitle>
            </CardHeader>
            <CardContent>
              {stats.recent_logs?.length === 0 ? (
                <p className="text-gray-500 text-center py-4">Henüz SMS gönderilmemiş</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left p-2">Öğrenci</th>
                        <th className="text-left p-2">Telefon</th>
                        <th className="text-left p-2">Gün</th>
                        <th className="text-left p-2">Tarih</th>
                        <th className="text-left p-2">Durum</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {stats.recent_logs?.map((log, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="p-2 font-medium">{log.student_name}</td>
                          <td className="p-2 text-gray-600">{log.student_phone}</td>
                          <td className="p-2">{log.reminder_day}. gün</td>
                          <td className="p-2 text-gray-600">{log.sent_at?.substring(0, 10)}</td>
                          <td className="p-2">
                            <span className={cn(
                              "px-2 py-1 rounded-full text-xs font-medium",
                              log.success ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                            )}>
                              {log.success ? '✅ Gönderildi' : '❌ Başarısız'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Inactive Students Tab */}
      {activeTab === 'students' && inactiveStudents && (
        <div className="space-y-6">
          {/* 7 Days */}
          <Card>
            <CardHeader className="bg-yellow-50">
              <CardTitle className="text-lg text-yellow-700">
                ⚠️ 7-13 Gün İnaktif ({inactiveStudents['7_days']?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {inactiveStudents['7_days']?.length === 0 ? (
                <p className="text-gray-500 text-center py-4">Bu aralıkta öğrenci yok</p>
              ) : (
                <div className="divide-y max-h-60 overflow-y-auto">
                  {inactiveStudents['7_days']?.map((s, idx) => (
                    <div key={idx} className="p-3 flex items-center justify-between hover:bg-gray-50">
                      <div>
                        <p className="font-medium">{s.name}</p>
                        <p className="text-sm text-gray-500">{s.phone}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-yellow-600 font-medium">{s.days_inactive} gün</p>
                        {s.last_sms_sent && (
                          <p className="text-xs text-gray-400">Son SMS: {s.last_sms_sent}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* 14 Days */}
          <Card>
            <CardHeader className="bg-orange-50">
              <CardTitle className="text-lg text-orange-700">
                🔶 14-29 Gün İnaktif ({inactiveStudents['14_days']?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {inactiveStudents['14_days']?.length === 0 ? (
                <p className="text-gray-500 text-center py-4">Bu aralıkta öğrenci yok</p>
              ) : (
                <div className="divide-y max-h-60 overflow-y-auto">
                  {inactiveStudents['14_days']?.map((s, idx) => (
                    <div key={idx} className="p-3 flex items-center justify-between hover:bg-gray-50">
                      <div>
                        <p className="font-medium">{s.name}</p>
                        <p className="text-sm text-gray-500">{s.phone}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-orange-600 font-medium">{s.days_inactive} gün</p>
                        {s.last_sms_sent && (
                          <p className="text-xs text-gray-400">Son SMS: {s.last_sms_sent}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* 30 Days */}
          <Card>
            <CardHeader className="bg-red-50">
              <CardTitle className="text-lg text-red-700">
                🔴 30+ Gün İnaktif ({inactiveStudents['30_days']?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {inactiveStudents['30_days']?.length === 0 ? (
                <p className="text-gray-500 text-center py-4">Bu aralıkta öğrenci yok</p>
              ) : (
                <div className="divide-y max-h-60 overflow-y-auto">
                  {inactiveStudents['30_days']?.map((s, idx) => (
                    <div key={idx} className="p-3 flex items-center justify-between hover:bg-gray-50">
                      <div>
                        <p className="font-medium">{s.name}</p>
                        <p className="text-sm text-gray-500">{s.phone}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-red-600 font-medium">{s.days_inactive} gün</p>
                        {s.last_sms_sent && (
                          <p className="text-xs text-gray-400">Son SMS: {s.last_sms_sent}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

// ==================== EMAIL REMINDER MANAGEMENT ====================
export function EmailReminderManagement() {
  const [settings, setSettings] = useState({
    enabled: true,
    reminder_days: [7, 14, 30],
    subject_template: ''
  });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [activeTab, setActiveTab] = useState('settings');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [settingsRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/admin/settings/email-reminder`, { withCredentials: true }),
        axios.get(`${API}/api/admin/email-reminder/stats`, { withCredentials: true })
      ]);
      setSettings(settingsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Error fetching email reminder data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/admin/settings/email-reminder`, settings, { withCredentials: true });
      alert('Ayarlar kaydedildi!');
    } catch (err) {
      alert('Kaydetme hatası: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRunNow = async () => {
    if (!window.confirm('E-posta hatırlatmalarını şimdi göndermek istediğinizden emin misiniz?')) return;
    setRunning(true);
    try {
      const res = await axios.post(`${API}/api/admin/email-reminder/run-now`, {}, { withCredentials: true });
      alert(`İşlem tamamlandı!\nGönderilen: ${res.data.sent}\nBaşarısız: ${res.data.failed}\nAtlanan: ${res.data.skipped}`);
      fetchData();
    } catch (err) {
      alert('Hata: ' + err.message);
    } finally {
      setRunning(false);
    }
  };

  const handleSendTest = async () => {
    if (!testEmail) {
      alert('Lütfen test e-posta adresi girin');
      return;
    }
    setSendingTest(true);
    try {
      await axios.post(`${API}/api/admin/email-reminder/send-test`, { email: testEmail }, { withCredentials: true });
      alert(`Test e-postası ${testEmail} adresine gönderildi!`);
    } catch (err) {
      alert('Hata: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSendingTest(false);
    }
  };

  const toggleDay = (day) => {
    const days = settings.reminder_days || [];
    if (days.includes(day)) {
      setSettings({ ...settings, reminder_days: days.filter(d => d !== day) });
    } else {
      setSettings({ ...settings, reminder_days: [...days, day].sort((a, b) => a - b) });
    }
  };

  if (loading) {
    return <div className="p-6 text-center text-gray-500">Yükleniyor...</div>;
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">📧 E-posta Bildirim Sistemi</h2>
        <div className="flex gap-2">
          <Button 
            onClick={handleRunNow} 
            disabled={running || !settings.enabled}
            className="bg-green-600 hover:bg-green-700"
          >
            {running ? 'Gönderiliyor...' : '🚀 Şimdi Gönder'}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <Button 
          variant={activeTab === 'settings' ? 'default' : 'outline'} 
          onClick={() => setActiveTab('settings')}
        >
          ⚙️ Ayarlar
        </Button>
        <Button 
          variant={activeTab === 'stats' ? 'default' : 'outline'} 
          onClick={() => setActiveTab('stats')}
        >
          📊 İstatistikler
        </Button>
        <Button 
          variant={activeTab === 'test' ? 'default' : 'outline'} 
          onClick={() => setActiveTab('test')}
        >
          🧪 Test
        </Button>
      </div>

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="space-y-6">
          {/* Enable/Disable */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Sistem Durumu</CardTitle>
            </CardHeader>
            <CardContent>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.enabled}
                  onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
                  className="w-5 h-5"
                />
                <span className={cn(
                  "font-medium",
                  settings.enabled ? "text-green-600" : "text-red-600"
                )}>
                  {settings.enabled ? '✅ E-posta Hatırlatma Aktif' : '❌ E-posta Hatırlatma Devre Dışı'}
                </span>
              </label>
            </CardContent>
          </Card>

          {/* Reminder Days */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Hatırlatma Günleri</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-500 mb-4">
                Öğrenci bu kadar gün giriş yapmadığında e-posta gönderilir
              </p>
              <div className="flex gap-3">
                {[7, 14, 21, 30, 45, 60].map(day => (
                  <button
                    key={day}
                    onClick={() => toggleDay(day)}
                    className={cn(
                      "px-4 py-2 rounded-lg font-medium transition-colors",
                      (settings.reminder_days || []).includes(day)
                        ? "bg-blue-600 text-white"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    )}
                  >
                    {day} Gün
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Subject Template */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">E-posta Konu Şablonu</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-500 mb-2">
                <code className="bg-gray-100 px-1 rounded">{'{name}'}</code> öğrenci adı ile değiştirilir
              </p>
              <Input
                value={settings.subject_template || ''}
                onChange={(e) => setSettings({ ...settings, subject_template: e.target.value })}
                placeholder="🛫 {name}, seni özledik! - AirAcademia"
              />
            </CardContent>
          </Card>

          {/* Email Preview */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">📨 E-posta Önizleme</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-gradient-to-r from-blue-500 to-blue-700 p-6 text-center text-white">
                  <div className="text-4xl mb-2">✈️</div>
                  <h3 className="text-xl font-bold">Seni Özledik!</h3>
                  <p className="text-blue-100 text-sm">AirAcademia Kabin Memuru Eğitimi</p>
                </div>
                <div className="p-6 bg-white">
                  <p className="text-lg mb-4">Merhaba <strong>[Öğrenci Adı]</strong>! 👋</p>
                  <p className="text-gray-600 mb-4">
                    Seni <strong>X gündür</strong> eğitim platformumuzda göremiyoruz. 
                    Kabin memurluğu hayalin seni bekliyor! 🌟
                  </p>
                  <div className="text-center my-6">
                    <span className="inline-block bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-3 rounded-full font-bold">
                      🚀 Eğitime Devam Et
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Button onClick={handleSave} disabled={saving} className="w-full">
            {saving ? 'Kaydediliyor...' : '💾 Ayarları Kaydet'}
          </Button>
        </div>
      )}

      {/* Stats Tab */}
      {activeTab === 'stats' && stats && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
              <CardContent className="p-4">
                <p className="text-blue-100 text-sm">Son 30 Gün Gönderilen</p>
                <p className="text-3xl font-bold">{stats.total_sent_30d}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-red-500 to-red-600 text-white">
              <CardContent className="p-4">
                <p className="text-red-100 text-sm">Başarısız</p>
                <p className="text-3xl font-bold">{stats.total_failed_30d}</p>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
              <CardContent className="p-4">
                <p className="text-green-100 text-sm">Başarı Oranı</p>
                <p className="text-3xl font-bold">
                  %{stats.total_sent_30d + stats.total_failed_30d > 0 
                    ? Math.round(stats.total_sent_30d / (stats.total_sent_30d + stats.total_failed_30d) * 100) 
                    : 100}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Recent Logs */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Son Gönderilen E-postalar</CardTitle>
            </CardHeader>
            <CardContent>
              {stats.recent_logs?.length === 0 ? (
                <p className="text-gray-500 text-center py-4">Henüz e-posta gönderilmemiş</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left p-2">Öğrenci</th>
                        <th className="text-left p-2">E-posta</th>
                        <th className="text-left p-2">Gün</th>
                        <th className="text-left p-2">Tarih</th>
                        <th className="text-left p-2">Durum</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {stats.recent_logs?.map((log, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="p-2 font-medium">{log.student_name}</td>
                          <td className="p-2 text-gray-600">{log.student_email}</td>
                          <td className="p-2">{log.reminder_day}. gün</td>
                          <td className="p-2 text-gray-600">{log.sent_at?.substring(0, 10)}</td>
                          <td className="p-2">
                            <span className={cn(
                              "px-2 py-1 rounded-full text-xs font-medium",
                              log.success ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                            )}>
                              {log.success ? '✅ Gönderildi' : '❌ Başarısız'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Test Tab */}
      {activeTab === 'test' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">🧪 Test E-postası Gönder</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-500 mb-4">
                E-posta şablonunu test etmek için kendinize veya başka bir adrese test e-postası gönderin.
              </p>
              <div className="flex gap-3">
                <Input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="test@example.com"
                  className="flex-1"
                />
                <Button onClick={handleSendTest} disabled={sendingTest}>
                  {sendingTest ? 'Gönderiliyor...' : '📤 Test Gönder'}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-4">
              <h4 className="font-bold text-amber-800 mb-2">⚠️ Önemli Bilgi</h4>
              <ul className="text-sm text-amber-700 space-y-1">
                <li>• E-postalar <strong>noreply@kabinmemurlugu.com</strong> adresinden gönderilir</li>
                <li>• Domain doğrulaması gerekebilir (Resend panelinden)</li>
                <li>• Test e-postası spam klasörüne düşebilir</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
